import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { Alert } from "@material-ui/lab";
import Pagination from "@material-ui/lab/Pagination";
import { Fragment, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import ContratoTooltip from "../../../../components/Contrato/ContratoTooltip";
import HelperDialog from "../../../../components/HelperDialog/HelperDialog";
import cuideoTheme from "../../../../containers/themes/defaultTheme";
import {
  CONTRATO_ESTADO_ACTIVO,
  CONTRATO_ESTADO_FIN_DE_CONTRATO,
  CONTRATO_ESTADO_SELECCIONADO,
} from "../../../../util/defaults/contrato";
import { useCandidateContracts } from "../../features/contract";
import ContractsTabHelp from "./ContractsTabHelp";
import { formatContractPeriod } from "./formatContractPeriod";
import FactCheckIcon from "../../../../assets/images/iconos/fact_check.svg";
import { Icon } from "../../../../components/shared/Icon";

const StyledWrapper = styled.div`
  margin: 1rem;
  max-width: 71.75rem;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 2rem 0 1rem;
  justify-content: space-between;
`;

const StyledTitle = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1.875rem;
  font-weight: 700;
`;

const StyledContent = styled.div``;

const StyledTable = styled(Table)`
  .MuiTableCell-body {
    border: none;
  }
`;

const StyledTableHead = styled(TableHead)``;

const StyledTableRow = styled(TableRow)``;

const StyledBodyRow = styled(TableRow)`
  background-color: #fff;
`;

const StyledHeadCell = styled(TableCell)`
  background-color: rgba(0, 72, 120, 0.1);
  color: ${cuideoTheme.palette.primary.light};
  font-size: 0.75rem;
  line-height: normal;
  font-weight: 700;
`;

const StyledBodyCell = styled(TableCell)`
  font-size: 0.875rem;
  color: ${cuideoTheme.palette.primary.main};
  line-height: 1.25;
`;

const StyledTableBody = styled(TableBody)``;

const StyledPagination = styled(Pagination)`
  margin: 1rem 0 0;
  .MuiPagination-ul {
    justify-content: center;
  }
`;

const StyledHelpIconButton = styled(IconButton)`
  .MuiSvgIcon-root {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const StyledLoadingWrapper = styled(TableCell)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
  width: 100%;
`;

const StyledErrorWrapper = styled(TableCell)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
`;

const StyledErrorAlert = styled(Alert)``;

const StyledEmptyText = styled(Typography)`
  font-size: 0.875rem;
  text-align: center;
`;

const ITEMS_PER_PAGE = 10;

const ContractsTab = (props: any) => {
  const { candidato } = props;
  const [page, setPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const [selectedContractId, setSelectedContractId] =
    useState<string | null>(null);

  const contractsQuery = useCandidateContracts({
    params: {
      candidateId: candidato.id,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
      estado: [
        CONTRATO_ESTADO_ACTIVO,
        CONTRATO_ESTADO_SELECCIONADO,
        CONTRATO_ESTADO_FIN_DE_CONTRATO,
      ],
      "order[createdAt]": "desc",
    },
  });

  const handlePageChange = (event: any, value: any) => setPage(value);

  const handleHelpOpen = () => {
    setHelpOpen(true);
  };

  const handleHelpClose = () => {
    setHelpOpen(false);
  };

  const status = contractsQuery.status;

  return (
    <StyledWrapper>
      <StyledHeader>
        <StyledTitle>
          <FormattedMessage
            id="ContractsTab.Contracts"
            defaultMessage="Contratos"
          />
        </StyledTitle>
        <StyledHelpIconButton color="primary" onClick={handleHelpOpen}>
          <HelpOutlineIcon />
        </StyledHelpIconButton>
      </StyledHeader>
      <StyledContent>
        <StyledTable>
          <StyledTableHead>
            <StyledTableRow>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.Contract"
                  defaultMessage="Contrato"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.Status"
                  defaultMessage="Estado"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.Customer"
                  defaultMessage="Cliente"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.ServiceSchedule"
                  defaultMessage="Horario Servicio"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.BeginAndEnd"
                  defaultMessage="Inicio y fin"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.Owner"
                  defaultMessage="Owner"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.EndReasonType"
                  defaultMessage="Tipo fin contrato"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.EndNote"
                  defaultMessage="Nota"
                />
              </StyledHeadCell>
              <StyledHeadCell>
                <FormattedMessage
                  id="ContractsTab.Valuation"
                  defaultMessage="Valoración"
                />
              </StyledHeadCell>
              <StyledHeadCell />
            </StyledTableRow>
          </StyledTableHead>
          <StyledTableBody>
            {status === "loading" && (
              <StyledBodyRow>
                <TableCell colSpan={10}>
                  <StyledLoadingWrapper>
                    <CircularProgress thickness={5} size={22} />
                  </StyledLoadingWrapper>
                </TableCell>
              </StyledBodyRow>
            )}
            {status === "error" && (
              <StyledBodyRow>
                <TableCell colSpan={10}>
                  <StyledErrorWrapper>
                    <StyledErrorAlert severity="error">
                      <FormattedMessage
                        id="ContractsTab.Ha habido un error"
                        defaultMessage="Ha habido un error"
                      />
                    </StyledErrorAlert>
                  </StyledErrorWrapper>
                </TableCell>
              </StyledBodyRow>
            )}
            {status === "success" && contractsQuery.data && (
              <>
                {contractsQuery.data["hydra:member"].length === 0 ? (
                  <StyledBodyRow>
                    <TableCell colSpan={10}>
                      <StyledEmptyText>
                        <FormattedMessage
                          id="ContractsTab.Este candidato/a no tiene ningún contrato"
                          defaultMessage="Este candidato/a no tiene ningún contrato"
                        />
                      </StyledEmptyText>
                    </TableCell>
                  </StyledBodyRow>
                ) : (
                  contractsQuery.data["hydra:member"].map((contract) => {
                    const displayValorationRow =
                      contract.Compromiso ||
                      contract.Experiencia ||
                      contract.Comunicaci_n ||
                      contract.Paciencia_y_Empatia ||
                      contract.Car_cter;
                    return (
                      <Fragment key={contract.id}>
                        <StyledBodyRow>
                          <StyledBodyCell>
                            <ContratoTooltip contrato={contract} />
                          </StyledBodyCell>
                          <StyledBodyCell>{contract.Estado}</StyledBodyCell>
                          <StyledBodyCell>
                            {contract._Nombre_del_cliente}
                          </StyledBodyCell>
                          <StyledBodyCell>
                            {contract.Horario_Servicio}
                          </StyledBodyCell>
                          <StyledBodyCell>
                            {formatContractPeriod(
                              contract.Fecha_de_inicio_real,
                              contract.Fecha_fin_contrato
                            )}
                          </StyledBodyCell>
                          <StyledBodyCell>{contract.ownerName}</StyledBodyCell>
                          <StyledBodyCell>
                            {contract.Tipo_fin_contrato}
                          </StyledBodyCell>
                          <StyledBodyCell>
                            {contract.A_ade_nota_del_candidato_a}
                          </StyledBodyCell>
                          <StyledBodyCell>
                            {contract.Valoracion_Cuideo_Candidata}
                          </StyledBodyCell>
                          <StyledBodyCell>
                            {displayValorationRow && (
                              <Icon
                                src={FactCheckIcon}
                                onClick={() => {
                                  setSelectedContractId(
                                    selectedContractId === contract.id
                                      ? null
                                      : contract.id
                                  );
                                }}
                              />
                            )}
                          </StyledBodyCell>
                        </StyledBodyRow>
                        {selectedContractId === contract.id && (
                          <StyledBodyRow
                            style={{
                              backgroundColor: cuideoTheme.palette.grey[300],
                            }}
                          >
                            <StyledBodyCell colSpan={10}>
                              <div>
                                <b>Valoración Cliente</b>
                                <div
                                  style={{
                                    marginTop: 16,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span>
                                    <b>Compromiso:</b> {contract.Compromiso}
                                  </span>
                                  <span>
                                    <b>Experiencia:</b> {contract.Experiencia}
                                  </span>
                                  <span>
                                    <b>Comunicación:</b>{" "}
                                    {contract.Comunicaci_n}
                                  </span>
                                  <span>
                                    <b>Paciencia y empatia:</b>{" "}
                                    {contract.Paciencia_y_Empatia}
                                  </span>
                                  <span>
                                    <b>Carácter:</b> {contract.Car_cter}
                                  </span>
                                </div>
                              </div>
                            </StyledBodyCell>
                          </StyledBodyRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </>
            )}
          </StyledTableBody>
        </StyledTable>
        {status === "success" &&
          contractsQuery.data &&
          contractsQuery.data["hydra:totalItems"] > 0 && (
            <StyledPagination
              count={Math.ceil(
                contractsQuery.data["hydra:totalItems"] / ITEMS_PER_PAGE
              )}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          )}
      </StyledContent>
      <HelperDialog
        open={helpOpen}
        onClose={handleHelpClose}
        text={<ContractsTabHelp />}
        title={
          <FormattedMessage id="ContractsTab.Help" defaultMessage="Ayuda" />
        }
        okLabel={
          <FormattedMessage id="ContractsTab.Accept" defaultMessage="Aceptar" />
        }
      />
    </StyledWrapper>
  );
};

export default ContractsTab;
